<template>
  <Popover
    v-slot="{ open }"
    class="relative inline-block text-left w-full px-2 z-10"
  >
    <PopoverButton
      ref="buttonBackgroundRequests"
      v-if="jobs?.length > 0"
      @click="handlerClick"
      class="w-full flex gap-x-2 items-center text-white border border-sky-300 border-l-0 border-r-0 focus:outline-none"
    >
      <div
        :class="{ 'bg-sky-400  border-white': open }"
        class="mx-1 w-full flex justify-center items-center gap-x-1 p-1 rounded-md my-1 cursor-pointer border border-transparent"
      >
        <ArrowPathIcon class="size-5" />
        <label class="text-xs font-normal cursor-pointer">{{
          t("common.components.backgroundProcess.title")
        }}</label>
      </div>
    </PopoverButton>
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <Teleport to="body">
        <PopoverPanel
          v-slot="{ close }"
          :class="[
            'fixed bottom-0 left-0 p-2 z-50 w-full  md:w-[360px]',
            isSidebarExpanded ? 'md:left-[200px]' : 'md:left-[70px]',
            isExpanded
              ? 'h-full overflow-y-auto'
              : 'h-[400px] overflow-y-hidden',
          ]"
        >
          <ProcessList
            :is-expanded="isExpanded"
            :class="[
              'rounded-xl bg-white text-gray-900 focus:outline-none shadow-xl border border-gray-200',
            ]"
            @toogle:expand="isExpanded = $event"
            @close:panel="close()"
          />
        </PopoverPanel>
      </Teleport>
    </transition>
  </Popover>
</template>

<script setup lang="ts">
import { reqGetUserJobs } from "@/common/helpers/api";
import { ref, watch } from "vue";
import { onMounted, inject, provide, computed } from "vue";
import { alertKey } from "@/common/plugins/alert";
import {
  formatStringToUTC,
  utcToLocalDateBulk,
} from "@/common/helpers/Chronos";
import { DATE_TIME_TEMPLATE_SECONDS } from "@/common/helpers/constants";
import { format } from "date-fns-tz";
import { jobsKey } from ".";
import { isSidebarExpandedKey } from "../.";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { ArrowPathIcon } from "@heroicons/vue/20/solid";
import ProcessList from "./ProcessList.vue";
import useTailwindBreakpoint from "@/common/composables/useTailwindBreakpoint";
import { useI18n } from "vue-i18n";
import ComponentComunicator from "@/common/helpers/ComponentComunicator.ts";
import debounce from "lodash-es/debounce";
import useErrorHandler from "@/common/composables/useErrorHandler";

const { t } = useI18n();
const errorHandler = useErrorHandler();
const jobs = ref<Auth.JobUI[]>([]);
let interval: number | null = null;
let intervalCount = 0;
const MAX_INTERVAL_COUNT: Readonly<number> = 20;
const { isBreakpointOrAbove, width } = useTailwindBreakpoint();
const isExpanded = ref(!isBreakpointOrAbove("md"));
const componentComunicator = new ComponentComunicator("backgroundRequests");
const buttonBackgroundRequests = ref<InstanceType<typeof PopoverButton> | null>(
  null
);

componentComunicator.listenChannel(handlerBackgroundRequestsUpdate);
const alert = inject(alertKey);
const isSidebarExpanded = inject(
  isSidebarExpandedKey,
  computed(() => false)
);
provide(jobsKey, jobs);

watch(width, () => (isExpanded.value = !isBreakpointOrAbove("md")));

onMounted(async () => {
  await requestUserJobs();
  interval = setInterval(intervalHandler, 60 * 1000);
});

async function requestUserJobs() {
  const today = format(
    new Date().setHours(0, 0, 0, 0),
    DATE_TIME_TEMPLATE_SECONDS
  );
  const todayUTC = formatStringToUTC(today, DATE_TIME_TEMPLATE_SECONDS);
  const queryParam = `?filter[started_after]=${todayUTC}&raw=true`;
  const jobsResp = await reqGetUserJobs(queryParam);
  jobs.value =
    jobsResp?.map((job) => ({
      ...job,
      ...(utcToLocalDateBulk<Auth.Job>(job, ["started_at"]) as Auth.JobUI),
    })) ?? [];
}

async function intervalHandler() {
  try {
    await requestUserJobs();
  } catch (exception) {
    console.error(exception);
    intervalCount += 1;
    if (interval && intervalCount > MAX_INTERVAL_COUNT) {
      intervalCount = 0;
      clearInterval(interval);
      console.error(`Request jobs pulling disabled becouse process just took
      ${MAX_INTERVAL_COUNT} max of tries`);
      alert?.error(
        [
          "El gestor de procesos en fondo, se ha desactivado debido a un error. Contacte tech{'@'}letsgoi.com",
        ],
        { autoCloseInMs: 10000 }
      );
    }
  }
}

function handlerBackgroundRequestsUpdate() {
  requestUserJobs().catch(
    async (e) => await errorHandler.handleApiExceptions(e)
  );
  buttonBackgroundRequests.value?.$el?.click();
}

const handlerClick = debounce(async (open) => {
  try {
    if (!open) await requestUserJobs();
  } catch (e) {
    await errorHandler.handleApiExceptions(e);
  }
}, 200);
</script>
